import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Download, RefreshCw, Copy, HelpCircle, Zap, CheckCircle2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";

export const CalendarSync = ({ slug }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const [feedUrl, setFeedUrl] = useState(null);
  const [gcal, setGcal] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("gcal");
    if (!status) return;
    if (status === "connected") toast.success(t("gcal_connected_toast"), { duration: 10000 });
    else toast.error(t("gcal_error_toast"));
    params.delete("gcal");
    const qs = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
  }, [t]);

  const publicFeed = `${process.env.REACT_APP_BACKEND_URL}/api/calendar/public/${slug}.ics`;

  const exportUrl = (period) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/associations/${slug}/calendar/export?period=${period}`;

  const loadData = () => {
    if (!user) return;
    if (!feedUrl) api.get(`/associations/${slug}/calendar/feed-url`).then(({ data }) => setFeedUrl(data.url)).catch(() => {});
    api.get(`/gcal/status`, { params: { slug } }).then(({ data }) => setGcal(data)).catch(() => {});
  };

  const effectiveFeed = user ? feedUrl : publicFeed;
  const webcalUrl = effectiveFeed ? effectiveFeed.replace(/^https?:\/\//, "webcal://") : null;

  const connectGcal = async () => {
    try {
      const { data } = await api.get(`/oauth/calendar/connect`, { params: { slug } });
      window.location.href = data.url;
    } catch {
      toast.error(t("gcal_error_toast"));
    }
  };

  const disconnectGcal = async () => {
    try {
      await api.post(`/gcal/disconnect`);
      setGcal({ connected: false, google_email: "" });
      toast.success(t("gcal_disconnected_toast"));
    } catch {
      toast.error(t("gcal_error_toast"));
    }
  };

  const syncNow = async () => {
    toast.info(t("gcal_syncing"));
    try {
      const { data } = await api.post(`/gcal/sync-now`);
      if (data.errors?.length) {
        toast.error(`${t("gcal_sync_partial").replace("{n}", data.synced)} — ${data.errors[0]}`, { duration: 20000 });
      } else {
        toast.success(t("gcal_sync_done").replace("{n}", data.synced), { duration: 10000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("gcal_error_toast"));
    }
  };

  const enableGcal = async () => {
    toast.info(t("gcal_syncing"));
    try {
      const { data } = await api.post(`/gcal/enable`, null, { params: { slug } });
      setGcal((g) => ({ ...g, enabled_for_slug: true }));
      if (data.errors?.length) {
        toast.error(`${t("gcal_sync_partial").replace("{n}", data.synced)} — ${data.errors[0]}`, { duration: 20000 });
      } else {
        toast.success(t("gcal_sync_done").replace("{n}", data.synced), { duration: 10000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("gcal_error_toast"));
    }
  };

  const openGoogle = () => {
    if (!webcalUrl) return;
    window.open(`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`, "_blank");
    toast.info(t("google_sync_hint"), { duration: 15000 });
  };

  const openApple = () => {
    if (!webcalUrl) return;
    window.location.href = webcalUrl;
  };

  const copyFeed = () => {
    if (!effectiveFeed) return;
    const done = () => toast.success(t("feed_copied"));
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(effectiveFeed).then(done).catch(done);
    else done();
  };

  return (
    <DropdownMenu onOpenChange={(open) => open && loadData()}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" data-testid="calendar-sync-btn" className="rounded-full">
          <CalendarPlus className="h-4 w-4 mr-2" /> {t("calendar_sync")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        {user && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">{t("gcal_auto_label")}</DropdownMenuLabel>
        {gcal?.connected ? (
          <>
            <div className="px-2 py-1.5 flex items-center gap-2 text-sm" data-testid="gcal-connected-status">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
              <span className="min-w-0 truncate">
                {t("gcal_connected")}
                {gcal.google_email && <span className="block text-xs text-muted-foreground truncate">{gcal.google_email}</span>}
              </span>
            </div>
            {gcal.enabled_for_slug === false && (
              <DropdownMenuItem data-testid="gcal-enable-btn" onClick={enableGcal} className="font-medium">
                <Zap className="h-4 w-4 mr-2 text-primary" /> {t("gcal_enable_assoc")}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem data-testid="gcal-sync-now-btn" onClick={syncNow}>
              <RefreshCw className="h-4 w-4 mr-2" /> {t("gcal_sync_now")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="gcal-disconnect-btn" onClick={disconnectGcal} className="text-destructive">
              <Unlink className="h-4 w-4 mr-2" /> {t("gcal_disconnect")}
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem data-testid="gcal-connect-btn" onClick={connectGcal} className="font-medium">
            <Zap className="h-4 w-4 mr-2 text-primary" /> {t("gcal_connect")}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel className="text-xs text-muted-foreground">{t("calendar_subscribe_label")}</DropdownMenuLabel>
        <DropdownMenuItem data-testid="subscribe-google" onClick={openGoogle}>
          <RefreshCw className="h-4 w-4 mr-2" /> {t("subscribe_google")}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="subscribe-apple" onClick={openApple}>
          <RefreshCw className="h-4 w-4 mr-2" /> {t("subscribe_apple")}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="copy-feed-url" onClick={copyFeed}>
          <Copy className="h-4 w-4 mr-2" /> {t("copy_feed")}
        </DropdownMenuItem>
        {user && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">{t("calendar_export_label")}</DropdownMenuLabel>
            <DropdownMenuItem data-testid="export-month-ics" onClick={() => window.open(exportUrl("month"), "_blank")}>
              <Download className="h-4 w-4 mr-2" /> {t("export_month")}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="export-year-ics" onClick={() => window.open(exportUrl("year"), "_blank")}>
              <Download className="h-4 w-4 mr-2" /> {t("export_year")}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="sync-help-link">
          <Link to="/aide-sync">
            <HelpCircle className="h-4 w-4 mr-2" /> {t("sync_help")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
